import { Component } from '@angular/core';
import { ItemWithSelection } from '@app/shared/models/item-with-selection';
import {
  ActionComponentNew,
  ActionResult,
  DataSource,
  ListView,
  ListViewProviderDirective,
  NotificationService,
  NtapGlobalConstants,
  shareReplayRefCount,
  Util
} from '@ntap/core';
import { Observable, mergeMap, map, tap, first, combineLatest } from 'rxjs';

@Component({
  selector: 'app-items-list-action',
  templateUrl: '../../../shared/components/actions/common-action-template.html'
})
export class ItemsListActionComponent extends ActionComponentNew<ActionResult> {
  viewName: string;
  selectedAll = false;

  constructor(
    protected override notificationService: NotificationService,
    protected dataSource: DataSource,
    protected listViewProvider: ListViewProviderDirective<ListView>
  ) {
    super(notificationService);
    this.addSubscription(
      combineLatest([this.listViewProvider.view$]).subscribe(
        ([viewMetadata]) => {
          this.viewName = viewMetadata.name;
        }
      )
    );
  }

  protected override initDisabledObservable(): void {
    this.isDisabled$ = this.listViewProvider.selectedItems$.pipe(
      map((selectedItems) => {
        return selectedItems.length <= 0;
      })
    );
  }

  protected isCloseAction() {
    const a = this.action as any;
    return !!a?.close_view;
  }

  public doExecuteWithParams(params: Record<string, any>) {
    let result$: Observable<ActionResult> =
      this.nextSelectedItemsOrError$().pipe(
        mergeMap((selectedItems: Array<unknown>) =>
          this.doExecuteWithSelectedItems(selectedItems, params)
        ),
        shareReplayRefCount(1) // make it hot
      );
    if (!this.isCloseAction()) {
      result$ = result$.pipe(tap(() => this.listViewProvider.refresh()));
    }
    return result$;
  }

  protected doExecute(): Observable<ActionResult> {
    let result$: Observable<ActionResult> =
      this.nextSelectedItemsOrError$().pipe(
        mergeMap((selectedItems: Array<any>) =>
          this.doExecuteWithSelectedItems(selectedItems)
        ),
        shareReplayRefCount(1) // make it hot
      );
    if (!this.isCloseAction()) {
      result$ = result$.pipe(tap(() => this.listViewProvider.refresh()));
    }
    return result$;
  }

  protected doExecuteWithSelectedItems(
    items: Array<unknown>,
    params?: Record<string, any>
  ) {
    let item: ItemWithSelection = this.getItemWithSelectionData(items);
    item[NtapGlobalConstants.ATTR_$parent] = (
      this.listViewProvider.viewState as any
    )[NtapGlobalConstants.ATTR_$parent];

    if (params) {
      item = { ...item, ...params };
    }

    return this.dataSource
      .executeAction(this.action.name, item)
      .pipe(map((result) => result as ActionResult));
  }

  protected getItemWithSelectionData(items: any[]): ItemWithSelection {
    return {
      $selected: Util.mapToBasicItemData(items),
      $listName: this.viewName,
      $filterCriteria: (this.listViewProvider?.viewState as any)?.[
        '$filterCriteria'
      ]
    };
  }

  private nextSelectedItemsOrError$() {
    return this.listViewProvider.selectedItems$.pipe(first());
  }
}
